// user-client/src/state/LobbyDataState.jsx
import React, { createContext, useContext, useMemo, useReducer } from 'react';

// =============================
// Lobby Debug Logger
// =============================
const LOBBY_DEBUG =
  typeof window !== 'undefined' && localStorage.getItem('LOBBY_DEBUG') === '1';
const LOBBY_DEBUG_VERBOSE =
  typeof window !== 'undefined' && localStorage.getItem('LOBBY_DEBUG_VERBOSE') === '1';

function dbgGroup(title, fn) {
  if (!LOBBY_DEBUG) return;
  try {
    console.groupCollapsed(title);
    fn?.();
  } catch (e) {
    console.warn('[LobbyDebug] log failed:', e);
  } finally {
    console.groupEnd();
  }
}

function summarizeDisplaySetsByCode(displaySetsByCode) {
  const by = displaySetsByCode && typeof displaySetsByCode === 'object' ? displaySetsByCode : {};
  const codes = Object.keys(by);
  let tgCount = 0;
  let ibCount = 0;
  let bundleObj = 0;
  let bundleArr = 0;
  for (const code of codes) {
    const v = by[code];
    if (Array.isArray(v)) bundleArr += 1;
    else if (v && typeof v === 'object') bundleObj += 1;
    // best-effort count
    const tableGroup = Array.isArray(v)
      ? v.find((x) => isTableGroupDisplaySetType(getDisplaySetType(x)))
      : (v?.table_group || v?.tableGroup || v?.group || v?.table_group_row || v?.tableGroupRow);
    const infoButtons = Array.isArray(v)
      ? v.filter((x) => isInformationDisplaySetType(getDisplaySetType(x)))
      : readInfoButtonsFromBundle(v).items;
    if (tableGroup) tgCount += 1;
    if (Array.isArray(infoButtons)) ibCount += infoButtons.length;
  }
  return { codes: codes.length, tgCount, ibCount, bundleObj, bundleArr, sampleCode: codes[0] || null };
}

/**
 * LobbyDataState
 * - Backend snapshot(getTableGroups)에서 내려오는:
 *   - tableGroups (TableListDB)
 *   - displaySetsByCode (DisplaySetDB grouped by display_code)
 *   - server_utc_now
 * 를 "원본 그대로" 전역 상태로 보관한다.
 *
 * - 화면(Home/Events/Notice/SchedulePanel)은 이 원본 상태를 꺼내서
 *   필요할 때만 파생(가공)해서 렌더링한다.
 *
 * Export:
 * - <LobbyDataProvider />
 * - useLobbyDataState()
 * - useLobbyDataDispatch()
 * - useLobbySelectors(): tableGroups, displaySetsByCode, allDisplaySets, serverUtcNow
 */

const LobbyDataStateContext = createContext(null);
const LobbyDataDispatchContext = createContext(null);

const initialState = {
  // backend snapshot
  serverUtcNow: null, // string(UTC ISO)
  tableGroups: [], // TableList rows
  displaySetsByCode: {}, // { [display_code]: { table_group, information_buttons[] } }

   // ✅ scheduler selection (LOCAL dayKey: YYYY-MM-DD)
  selectedDayKey: null,

  // derived cache (optional, for convenience/perf)
  allDisplaySets: [], // flat list (table_group + information_buttons)
  lastHydratedAt: null, // number(ms)
  // 최초 로비 부트스트랩 상태. 백그라운드 재조회는 ready 상태를 유지한다.
  bootstrapStatus: 'idle', // idle | loading | ready | error
};

function pad2(n) {
  return String(n).padStart(2, '0');
}
function toDayKeyLocal(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

const INFORMATION_BUTTON_KEYS = [
  'information_buttons',
  'informationButtons',
  'info_buttons',
  'infoButtons',
  'information',
  'information_button',
  'informationButton',
];

const INFORMATION_DISPLAY_TYPES = new Set([
  'information_button',
  'information_buttons',
  'info_button',
  'info_buttons',
  'infoButtons',
  'information',
  'info',
  'information_card',
  'timer',
  'timer_card',
  'image_url',
  'image_url_card',
  'image',
  'image_card',
  'link',
  'link_card',
  'url',
  'url_card',
  'banner',
  'banner_card',
  'notice',
  'notice_card',
]);

const DISPLAY_ROW_ARRAY_KEYS = [
  'items',
  'rows',
  'displaySets',
  'display_sets',
  'displaySetRows',
  'display_set_rows',
  'display_set_items',
  'list',
  'data',
];

function getDisplaySetType(row) {
  return String(
    row?.type ??
    row?.kind ??
    row?.display_type ??
    row?.displayType ??
    row?.card_type ??
    row?.cardType ??
    row?.button_type ??
    row?.buttonType ??
    ''
  ).trim().toLowerCase();
}

function isTableGroupDisplaySetType(type) {
  const t = String(type || '').trim().toLowerCase();
  return t === 'table_group' || t === 'tablegroup' || t === 'group' || t === 'table';
}

function isInformationDisplaySetType(type) {
  const t = String(type || '').trim().toLowerCase();
  if (!t) return false;
  return INFORMATION_DISPLAY_TYPES.has(t);
}

function extractDisplayRows(value, depth = 0) {
  if (!value || depth > 3) return null;
  if (Array.isArray(value)) return value;
  if (typeof value !== 'object') return null;

  for (const key of DISPLAY_ROW_ARRAY_KEYS) {
    const candidate = value[key];
    if (Array.isArray(candidate)) return candidate;
  }

  for (const key of DISPLAY_ROW_ARRAY_KEYS) {
    const candidate = value[key];
    const nested = extractDisplayRows(candidate, depth + 1);
    if (Array.isArray(nested)) return nested;
  }

  return null;
}

function groupDisplayRowsByCode(rows) {
  const out = {};
  if (!Array.isArray(rows)) return out;
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const code = String(row.display_code || row.displayCode || row.display_set_code || row.displaySetCode || '').trim();
    if (!code) continue;
    if (!out[code]) out[code] = [];
    out[code].push(row);
  }
  return out;
}

function hasOwn(obj, key) {
  return !!obj && Object.prototype.hasOwnProperty.call(obj, key);
}

function readInfoButtonsFromBundle(bundle) {
  if (!bundle || typeof bundle !== 'object' || Array.isArray(bundle)) {
    return { hasKey: false, items: [] };
  }

  for (const key of INFORMATION_BUTTON_KEYS) {
    if (hasOwn(bundle, key)) {
      const raw = bundle[key];
      if (Array.isArray(raw)) return { hasKey: true, items: raw };
      if (raw && typeof raw === 'object') return { hasKey: true, items: [raw] };
      return { hasKey: true, items: [] };
    }
  }

  return { hasKey: false, items: [] };
}

function debugNormalizedDisplaySet(display_code, rawBundle, normalized) {
  if (process.env.NODE_ENV === 'production') return;
  try {
    console.debug('[LobbyDisplaySet] normalized', {
      display_code,
      hasTableGroup: !!normalized?.table_group,
      informationButtonCount: Array.isArray(normalized?.information_buttons)
        ? normalized.information_buttons.length
        : 0,
      rawKeys: rawBundle && typeof rawBundle === 'object' ? Object.keys(rawBundle) : [],
    });
  } catch {}
}

function safeBundle(bundle, previousBundle = null, display_code = null) {
  const previous = previousBundle ? safeBundle(previousBundle) : null;
  if (!bundle) return { table_group: previous?.table_group || null, information_buttons: previous?.information_buttons || [] };

  // ✅ 배열 번들 또는 {items/displaySets/display_sets/...} 변형 지원
  const rowArray = extractDisplayRows(bundle);
  if (Array.isArray(rowArray)) {
    const tg = rowArray.find((x) => isTableGroupDisplaySetType(getDisplaySetType(x))) || previous?.table_group || null;
    const ibs = rowArray.filter((x) => isInformationDisplaySetType(getDisplaySetType(x)));
    const normalized = { table_group: tg, information_buttons: ibs };
    debugNormalizedDisplaySet(display_code, bundle, normalized);
    return normalized;
  }

  if (typeof bundle !== 'object') {
    return { table_group: previous?.table_group || null, information_buttons: previous?.information_buttons || [] };
  }

  const rawType = getDisplaySetType(bundle);
  if (isTableGroupDisplaySetType(rawType) || isInformationDisplaySetType(rawType)) {
    const normalized = {
      table_group: isTableGroupDisplaySetType(rawType) ? bundle : previous?.table_group || null,
      information_buttons: isInformationDisplaySetType(rawType) ? [bundle] : (previous?.information_buttons || []),
    };
    debugNormalizedDisplaySet(display_code, bundle, normalized);
    return normalized;
  }

  const tg = bundle.table_group || bundle.tableGroup || bundle.group || bundle.table_group_row || bundle.tableGroupRow || previous?.table_group || null;
  const infoRead = readInfoButtonsFromBundle(bundle);
  const ibs = infoRead.hasKey ? infoRead.items : (previous?.information_buttons || []);

  const normalized = {
    table_group: tg || null,
    information_buttons: Array.isArray(ibs) ? ibs : [],
  };

  debugNormalizedDisplaySet(display_code, bundle, normalized);
  return normalized;
}

function flattenDisplaySets(displaySetsByCode) {
  const out = [];
  if (!displaySetsByCode || typeof displaySetsByCode !== 'object') return out;

  for (const code of Object.keys(displaySetsByCode)) {
    const b = safeBundle(displaySetsByCode[code]);
    if (b.table_group) out.push(b.table_group);
    if (b.information_buttons.length) out.push(...b.information_buttons);
  }
  return out;
}

/**
 * Upsert one DisplaySet item into displaySetsByCode.
 * Expected ds shape:
 * - id
 * - display_code
 * - type: 'table_group' | 'information_button'
 * - start_at / end_at etc... (whatever backend provides)
 */
function upsertOne(displaySetsByCode, ds) {
  if (!ds || typeof ds !== 'object') return displaySetsByCode;
  const code = ds.display_code;
  if (!code) return displaySetsByCode;

  const next = { ...displaySetsByCode };
  const curBundle = safeBundle(next[code]);
  const bundle = { ...curBundle };

  const dsType = getDisplaySetType(ds);

  if (isTableGroupDisplaySetType(dsType)) {
    bundle.table_group = ds;
  } else if (isInformationDisplaySetType(dsType)) {
    const arr = Array.isArray(bundle.information_buttons) ? [...bundle.information_buttons] : [];
    const idx = arr.findIndex((x) => x && x.id === ds.id);
    if (idx >= 0) arr[idx] = ds;
    else arr.push(ds);
    // 정렬이 필요하면 index/id 기준 정렬(백엔드도 이미 정렬해서 줌)
    arr.sort((a, b) => {
      const ai = typeof a?.index === 'number' ? a.index : 0;
      const bi = typeof b?.index === 'number' ? b.index : 0;
      if (ai !== bi) return ai - bi;
      const aid = typeof a?.id === 'number' ? a.id : Number.MAX_SAFE_INTEGER;
      const bid = typeof b?.id === 'number' ? b.id : Number.MAX_SAFE_INTEGER;
      return aid - bid;
    });
    bundle.information_buttons = arr;
  } else {
    // unknown type -> keep as-is (or ignore)
    return displaySetsByCode;
  }

  next[code] = bundle;
  return next;
}

/**
 * Remove one DisplaySet item.
 * payload can be:
 * - { id, display_code, type }
 * - OR { id } only (we'll scan)
 */
function removeOne(displaySetsByCode, payload) {
  if (!payload) return displaySetsByCode;

  const { id, display_code, type } = payload;

  // Fast path: has display_code
  if (display_code && displaySetsByCode?.[display_code]) {
    const next = { ...displaySetsByCode };
    const b = safeBundle(next[display_code]);
    const bundle = { ...b };

    if (type === 'table_group') {
      if (bundle.table_group && bundle.table_group.id === id) bundle.table_group = null;
    } else if (isInformationDisplaySetType(type)) {
      bundle.information_buttons = (bundle.information_buttons || []).filter((x) => x && x.id !== id);
    } else {
      // type not provided: remove from both places
      if (bundle.table_group && bundle.table_group.id === id) bundle.table_group = null;
      bundle.information_buttons = (bundle.information_buttons || []).filter((x) => x && x.id !== id);
    }

    next[display_code] = bundle;
    return next;
  }

  // Slow path: scan all codes by id
  if (id == null) return displaySetsByCode;
  const next = { ...displaySetsByCode };
  let changed = false;

  for (const code of Object.keys(next)) {
    const b = safeBundle(next[code]);
    let modified = false;

    const bundle = { ...b };

    if (bundle.table_group && bundle.table_group.id === id) {
      bundle.table_group = null;
      modified = true;
    }

    const beforeLen = bundle.information_buttons.length;
    bundle.information_buttons = (bundle.information_buttons || []).filter((x) => x && x.id !== id);
    if (bundle.information_buttons.length !== beforeLen) modified = true;

    if (modified) {
      next[code] = bundle;
      changed = true;
    }
  }

  return changed ? next : displaySetsByCode;
}

function reducer(state, action) {
  switch (action.type) {
    case 'LOBBY_BOOTSTRAP_LOADING': {
      // 최초 화면에서만 loading으로 전환한다. 이미 정상 snapshot이 있으면
      // 언어/네트워크 재동기화가 전체화면 로더를 다시 띄우지 않게 ready를 유지한다.
      if (state.lastHydratedAt) return state;
      if (state.bootstrapStatus === 'loading') return state;
      return { ...state, bootstrapStatus: 'loading' };
    }

    case 'LOBBY_BOOTSTRAP_READY': {
      if (state.bootstrapStatus === 'ready') return state;
      return { ...state, bootstrapStatus: 'ready' };
    }

    case 'LOBBY_BOOTSTRAP_ERROR': {
      // 이미 완전히 준비된 뒤의 일시 네트워크 오류는 기존 화면을 유지한다.
      // 최초 snapshot은 받았지만 구독 준비가 실패한 경우에는 error로 settle하여 로더가 고착되지 않게 한다.
      if (state.bootstrapStatus === 'ready' && state.lastHydratedAt) return state;
      if (state.bootstrapStatus === 'error') return state;
      return { ...state, bootstrapStatus: 'error' };
    }
    /**
     * INIT_SNAPSHOT: backend getTableGroups 결과를 그대로 저장
     * payload:
     * - { tableGroups, displaySetsByCode, serverUtcNow }
     */
    case 'INIT_SNAPSHOT': {
      const tableGroups = Array.isArray(action.payload?.tableGroups) ? action.payload.tableGroups : [];
      const directDisplaySetsByCode =
        action.payload?.displaySetsByCode && typeof action.payload.displaySetsByCode === 'object' && !Array.isArray(action.payload.displaySetsByCode)
          ? action.payload.displaySetsByCode
          : {};
      const flatRows =
        extractDisplayRows(action.payload?.displaySets) ||
        extractDisplayRows(action.payload?.display_sets) ||
        extractDisplayRows(action.payload?.displaySetRows) ||
        extractDisplayRows(action.payload?.display_set_rows) ||
        extractDisplayRows(action.payload?.items) ||
        [];
      const rawDisplaySetsByCode = Object.keys(directDisplaySetsByCode || {}).length
        ? directDisplaySetsByCode
        : groupDisplayRowsByCode(flatRows);

  // ✅ 여기서 1회 정규화: 이후 파생값(flatten/selector)에서 형태 불일치로 씹히는 문제 방지
  // ✅ information 관련 키가 누락된 불완전 snapshot이 늦게 도착해도 기존 information_buttons는 보존
  const displaySetsByCode = {};
  for (const code of Object.keys(rawDisplaySetsByCode)) {
    displaySetsByCode[code] = safeBundle(
      rawDisplaySetsByCode[code],
      state.displaySetsByCode?.[code] || null,
      code
    );
  }

  for (const g of tableGroups) {
    const code = String(g?.display_code || g?.displayCode || g?.display_set_code || '').trim();
    if (!code || displaySetsByCode[code] || !state.displaySetsByCode?.[code]) continue;
    displaySetsByCode[code] = safeBundle(state.displaySetsByCode[code], null, code);
  }
            const serverUtcNow =
        action.payload?.serverUtcNow ??
        action.payload?.server_utc_now ??
        action.payload?.serverUtc ??
         null;

      const allDisplaySets = flattenDisplaySets(displaySetsByCode);

     // ✅ 기본 선택일은 항상 브라우저 로컬 기준 오늘 날짜로 시작한다.
      // 서버 UTC now는 DisplaySet 활성 시간 판정/표시에만 사용하고,
      // 스케줄러의 로그인 기본 날짜는 유저 로컬 날짜를 따른다.
      const selectedDayKey =
        state.selectedDayKey ||
        toDayKeyLocal(new Date());

    dbgGroup('[LobbyDebug] INIT_SNAPSHOT (raw from backend)', () => {
        console.log('payload keys:', Object.keys(action.payload || {}));
        console.log('serverUtcNow:', serverUtcNow);
        console.log('tableGroups.length:', tableGroups.length);
        console.log('tableGroups sample[0]:', tableGroups[0] || null);
        console.log('displaySetsByCode summary:', summarizeDisplaySetsByCode(displaySetsByCode));
        const sampleCode = Object.keys(displaySetsByCode || {})[0];
        if (sampleCode) console.log('displaySetsByCode sample bundle:', sampleCode, displaySetsByCode[sampleCode]);
        console.log('flatten(allDisplaySets).length:', allDisplaySets.length);
        console.log('selectedDayKey(init):', selectedDayKey);
        if (LOBBY_DEBUG_VERBOSE) {
          console.log('RAW payload.tableGroups:', action.payload?.tableGroups);
          console.log('RAW payload.displaySetsByCode:', action.payload?.displaySetsByCode);
        }
      });

      return {
        ...state,
        serverUtcNow,
        tableGroups,
        displaySetsByCode,
        allDisplaySets,
        selectedDayKey,
        lastHydratedAt: Date.now(),
      };
    }

    /**
     * SET_TABLE_GROUPS: tableGroups만 갱신
     */
    case 'SET_TABLE_GROUPS': {
      const tableGroups = Array.isArray(action.payload) ? action.payload : [];
      return { ...state, tableGroups };
    }

    /**
     * PATCH_TABLE_GROUP_RUNTIME: 활성 테이블/플레이어 수치만 display_code 기준으로 병합
     * payload: [{ display_code, active_tables, player_count }] 또는 { groups: [...] }
     */
    case 'PATCH_TABLE_GROUP_RUNTIME': {
      const rows = Array.isArray(action.payload)
        ? action.payload
        : (Array.isArray(action.payload?.groups) ? action.payload.groups : []);
      if (!Array.isArray(state.tableGroups) || !state.tableGroups.length) return state;

      // runtimeSummary는 전체 활성 테이블 스냅샷이다. 목록에 없는 그룹은 현재 활성 테이블 0개로 본다.
      const byCode = new Map();
      for (const row of rows) {
        const code = String(row?.display_code || row?.displayCode || '').trim();
        if (!code) continue;
        byCode.set(code, row);
      }
      let changed = false;
      const tableGroups = state.tableGroups.map((group) => {
        const code = String(group?.display_code || group?.displayCode || '').trim();
        const runtime = byCode.get(code) || null;

        const activeTables = Math.max(0, Number(runtime?.active_tables ?? runtime?.activeTableCount ?? 0) || 0);
        const playerCount = Math.max(0, Number(runtime?.player_count ?? runtime?.current_players ?? 0) || 0);
        const prevActive = Number(group?.active_tables ?? group?.active_table_count ?? 0) || 0;
        const prevPlayers = Number(group?.player_count ?? group?.current_players ?? 0) || 0;
        if (prevActive === activeTables && prevPlayers === playerCount) return group;

        changed = true;
        return {
          ...group,
          active_tables: activeTables,
          active_table_count: activeTables,
          player_count: playerCount,
          current_players: playerCount,
        };
      });

      return changed ? { ...state, tableGroups } : state;
    }

    /**
     * SET_SERVER_UTC_NOW: serverUtcNow만 갱신
     */
    case 'SET_SERVER_UTC_NOW': {
      const serverUtcNow = action.payload ?? null;
      return { ...state, serverUtcNow, lastHydratedAt: Date.now() };
    }

       /**
     * SET_SELECTED_DAY_KEY: 스케줄 패널(1/2번)에서 선택된 날짜(로컬 dayKey)
     * payload: { selectedDayKey: 'YYYY-MM-DD' }
     */
    case 'SET_SELECTED_DAY_KEY': {
      const selectedDayKey = action.payload?.selectedDayKey ?? null;
      return { ...state, selectedDayKey };
    }

    /**
     * RESET_SELECTED_DAY_TO_LOCAL_TODAY:
     * 로그인/로비 진입 시 스케줄러 기본 날짜를 브라우저 로컬 오늘로 초기화한다.
     * 이후 사용자가 날짜를 직접 선택하면 TABLE_GROUPS_UPDATED / snapshot refresh에서는 유지된다.
     */
    case 'RESET_SELECTED_DAY_TO_LOCAL_TODAY': {
      return { ...state, selectedDayKey: toDayKeyLocal(new Date()) };
    }

    /**
     * UPSERT_DISPLAY_SET: 소켓/HTTP로 단일 ds 업데이트 반영
     * payload: display set row
     */
    case 'UPSERT_DISPLAY_SET': {
      const ds = action.payload;
      const nextByCode = upsertOne(state.displaySetsByCode, ds);
      if (nextByCode === state.displaySetsByCode) return state;

      dbgGroup('[LobbyDebug] UPSERT_DISPLAY_SET', () => {
        console.log('ds:', ds);
        console.log('ds.display_code:', ds?.display_code, 'type:', ds?.type, 'id:', ds?.id);
        console.log('ds.time:', ds?.time ?? null);
      });

      return {
        ...state,
        displaySetsByCode: nextByCode,
        allDisplaySets: flattenDisplaySets(nextByCode),
      };
    }

    /**
     * UPSERT_BUNDLE: 특정 display_code의 bundle을 통째로 교체
     * payload: { display_code, table_group, information_buttons }
     */
    case 'UPSERT_BUNDLE': {
      const p = action.payload || {};
      const code = p.display_code;
      if (!code) return state;

      const nextByCode = { ...state.displaySetsByCode };
      nextByCode[code] = safeBundle(p, state.displaySetsByCode?.[code] || null, code);

    dbgGroup('[LobbyDebug] UPSERT_BUNDLE (refreshOne result)', () => {
        console.log('display_code:', code);
        console.log('table_group:', nextByCode[code]?.table_group || null);
        console.log('information_buttons.length:', nextByCode[code]?.information_buttons?.length || 0);
        console.log('table_group.time:', nextByCode[code]?.table_group?.time ?? null);
        if (LOBBY_DEBUG_VERBOSE) console.log('RAW bundle payload:', p);
      });

      return {
        ...state,
        displaySetsByCode: nextByCode,
        allDisplaySets: flattenDisplaySets(nextByCode),
      };
    }

    /**
     * REMOVE_DISPLAY_SET: 단일 ds 삭제 반영
     * payload: { id, display_code?, type? }
     */
    case 'REMOVE_DISPLAY_SET': {
      const nextByCode = removeOne(state.displaySetsByCode, action.payload);
      if (nextByCode === state.displaySetsByCode) return state;

      return {
        ...state,
        displaySetsByCode: nextByCode,
        allDisplaySets: flattenDisplaySets(nextByCode),
      };
    }

    /**
     * CLEAR: 로그아웃/세션 종료 등 전체 비우기
     */
    case 'CLEAR': {
      return { ...initialState };
    }

    default:
      return state;
  }
}

export function LobbyDataProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <LobbyDataStateContext.Provider value={state}>
      <LobbyDataDispatchContext.Provider value={dispatch}>
        {children}
      </LobbyDataDispatchContext.Provider>
    </LobbyDataStateContext.Provider>
  );
}

export function useLobbyDataState() {
  const v = useContext(LobbyDataStateContext);
  if (!v) {
    throw new Error('useLobbyDataState must be used within <LobbyDataProvider />');
  }
  return v;
}

export function useLobbyDataDispatch() {
  const v = useContext(LobbyDataDispatchContext);
  if (!v) {
    throw new Error('useLobbyDataDispatch must be used within <LobbyDataProvider />');
  }
  return v;
}

/**
 * Convenience selector hook
 * - allDisplaySets는 이미 state에 캐시되어 있지만,
 *   혹시 나중에 캐시를 제거해도 여기만 유지하면 됨.
 */
export function useLobbySelectors() {
  const state = useLobbyDataState();

  return useMemo(() => {
    return {
      serverUtcNow: state.serverUtcNow,
      tableGroups: state.tableGroups,
      displaySetsByCode: state.displaySetsByCode,
      allDisplaySets: state.allDisplaySets,
      selectedDayKey: state.selectedDayKey,
      lastHydratedAt: state.lastHydratedAt,
      bootstrapStatus: state.bootstrapStatus,
    };
  }, [
    state.serverUtcNow,
    state.tableGroups,
    state.displaySetsByCode,
    state.allDisplaySets,
    state.selectedDayKey,
    state.lastHydratedAt,
    state.bootstrapStatus,
  ]);
}
